export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 200;

const COMMON_FRAGMENTS = [
  'password',
  'contrasena',
  'qwerty',
  '123456',
  'admin123',
  'bienvenido',
  'welcome',
  'iloveyou',
] as const;

export interface PasswordContext {
  email?: string | null;
  nombre?: string | null;
}

export interface PasswordCheck {
  id: 'length' | 'uppercase' | 'lowercase' | 'number' | 'symbol' | 'personal' | 'spaces';
  label: string;
  met: boolean;
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

function personalFragments(context: PasswordContext): string[] {
  const fragments = new Set<string>();
  const email = context.email?.trim().toLowerCase() ?? '';
  const emailLocal = email.includes('@') ? (email.split('@')[0] ?? '') : email;
  if (emailLocal.length >= 3) fragments.add(normalize(emailLocal));

  const nombre = context.nombre?.trim() ?? '';
  if (nombre.length >= 4) fragments.add(normalize(nombre).replace(/\s+/g, ''));
  for (const token of nombre.split(/\s+/)) {
    if (token.length >= 4) fragments.add(normalize(token));
  }

  return [...fragments].filter((fragment) => fragment.length >= 3);
}

export function passwordChecks(
  password: string,
  context: PasswordContext = {},
): PasswordCheck[] {
  const normalized = normalize(password);
  const personal = personalFragments(context);
  const containsPersonal = personal.some((fragment) => normalized.includes(fragment));
  const containsCommon = COMMON_FRAGMENTS.some((fragment) => normalized.includes(fragment));

  return [
    {
      id: 'length',
      label: `Al menos ${PASSWORD_MIN_LENGTH} caracteres`,
      met: password.length >= PASSWORD_MIN_LENGTH,
    },
    {
      id: 'uppercase',
      label: 'Una letra mayúscula',
      met: /\p{Lu}/u.test(password),
    },
    {
      id: 'lowercase',
      label: 'Una letra minúscula',
      met: /\p{Ll}/u.test(password),
    },
    {
      id: 'number',
      label: 'Un número',
      met: /\p{N}/u.test(password),
    },
    {
      id: 'symbol',
      label: 'Un símbolo (!@#$%…)',
      met: /[^\p{L}\p{N}\s]/u.test(password),
    },
    {
      id: 'personal',
      label: 'Sin tu correo, tu nombre ni palabras comunes',
      met: !containsPersonal && !containsCommon,
    },
    {
      id: 'spaces',
      label: 'Sin espacios al inicio o al final',
      met: password.length > 0 && password === password.trim(),
    },
  ];
}

export function passwordIssues(
  password: string,
  context: PasswordContext = {},
): string[] {
  return passwordChecks(password, context)
    .filter((check) => !check.met)
    .map((check) => check.label);
}

export function passwordStrength(checks: PasswordCheck[]): {
  score: number;
  label: 'Débil' | 'Media' | 'Fuerte';
  tone: 'weak' | 'medium' | 'strong';
} {
  const score = checks.filter((check) => check.met).length;
  const ratio = checks.length === 0 ? 0 : score / checks.length;
  if (ratio >= 1) return { score, label: 'Fuerte', tone: 'strong' };
  if (ratio >= 0.6) return { score, label: 'Media', tone: 'medium' };
  return { score, label: 'Débil', tone: 'weak' };
}
